import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { accountApi } from "@/api/account";
import { ApiError } from "@/api/types";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/layout/auth-shell";

export const Route = createFileRoute("/recover-account")({
  component: RecoverAccountPage,
});

type State = "idle" | "working" | "done" | "error";

function RecoverAccountPage() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [state, setState] = useState<State>("idle");
  const [err, setErr] = useState<string | null>(null);

  async function restore() {
    setState("working");
    setErr(null);
    try {
      await accountApi.cancelDeletion(token);
      setState("done");
    } catch (e) {
      setState("error");
      if (e instanceof ApiError && e.status === 400) {
        setErr("This recovery link is invalid or has expired.");
      } else {
        setErr("Something went wrong. Please try again.");
      }
    }
  }

  if (!token) {
    return (
      <AuthShell title="Restore account" subtitle="Cancel a pending account deletion.">
        <p role="alert" className="text-sm text-fail">
          This link is missing its recovery token. Use the exact link from the deletion email.
        </p>
      </AuthShell>
    );
  }

  if (state === "done") {
    return (
      <AuthShell
        title="Account restored"
        subtitle="Your account is active again."
        footer={
          <a href="/login" className="font-medium text-brand hover:underline">
            Continue to sign in
          </a>
        }
      >
        <p className="text-sm text-ok">
          The pending deletion was cancelled and all your data is intact. You can sign in as usual.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Restore account"
      subtitle="Your account is scheduled for deletion. Restore it to keep your data."
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Clicking below cancels the pending deletion and restores full access. If you meant to
          delete your account, you can ignore this and it will be erased after the grace period.
        </p>
        {err && (
          <p role="alert" className="text-sm text-fail">
            {err}
          </p>
        )}
        <Button
          onClick={() => void restore()}
          disabled={state === "working"}
          className="w-full bg-brand text-primary-foreground hover:bg-brand/90"
        >
          {state === "working" ? "Restoring…" : "Restore my account"}
        </Button>
      </div>
    </AuthShell>
  );
}
